import React from "react";
import Input from "@cs/components/src/Input";
import Select from "@cs/components/src/Select";
import {
  actionContextOptions,
  actionTypeOptions,
  categoryOptions,
  categoryOptionsSegments,
  componentTypeInfo,
  objectTypeOptions,
  sortByOption,
  webhookEventTypeOptions,
  webhookOperationOptions,
  webhookRealmOptions,
} from "./utils";
import Section from "@cs/components/src/Section";
import WebhookQueryInput from "./WebhookQueryInput";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import CronInput from "./CronInput";
import Checkbox from "@cs/components/src/Checkbox";

interface Props {
  value: PluginConfigItem;
  onChange: (value: PluginConfigItem) => void;
}

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 10px;
`;

const Description = styled.div`
  color: var(--dark700);
  margin-bottom: 20px;
  font-size: 13px;
`;

const ComponentForm = (props: Props) => {
  const { value: pluginConfig, onChange } = props;
  const { componentId: id } = useParams();

  const component = pluginConfig.components.find(({ id: i }) => i === id);

  const update =
    (
      key:
        | keyof CustomActionConfig
        | keyof WebhookConfig
        | keyof ScheduleConfig
        | keyof MetadataViewConfig
        | keyof ListInputConfig
    ) =>
    (value: unknown) => {
      onChange({
        ...pluginConfig,
        components: pluginConfig.components.map((component) => {
          if (component.id === id) {
            return {
              ...component,
              [key]: value,
            };
          } else {
            return component;
          }
        }),
      });
    };

  if (!component) {
    return <></>;
  }

  const { title, color } = componentTypeInfo(component.type);

  return (
    <Section title={title} color={color} pad>
      {component.type === "metadata_view_config" && (
        <Description>
          This is for a fully managed metadata view. If you want a metadata view
          the customer can edit, you should create that separately in Iconik.
        </Description>
      )}
      <Input
        label="Name"
        required
        value={component.title}
        onChange={update("title")}
        autoFocus
      />
      {component.type === "custom_action_config" && (
        <>
          <Select
            label="Contexts"
            required
            options={actionContextOptions}
            value={component.contexts}
            onChange={(contexts) =>
              update("contexts")(
                contexts
                  .sort(sortByOption(actionContextOptions))
                  .map(({ value }) => value ?? "")
              )
            }
            isMulti
          />
          <Select
            label="Type"
            required
            value={component.requestType}
            options={actionTypeOptions}
            onChange={({ value }) => update("requestType")(value)}
          />
        </>
      )}
      {component.type !== "metadata_view_config" &&
        component.type !== "list_input_config" && (
          <>
            <Input
              label={
                component.type === "custom_action_config" &&
                component.requestType === "OPEN"
                  ? "URL"
                  : "Script"
              }
              required
              value={component.script ?? ""}
              onChange={update("script")}
            />
          </>
        )}

      {component.type === "webhook_config" && (
        <>
          <Row>
            <Select
              label="Event Type"
              options={webhookEventTypeOptions}
              value={component.eventType}
              onChange={({ value }) => update("eventType")(value)}
            />
            <Select
              label="Realm"
              options={webhookRealmOptions}
              value={component.realm}
              onChange={({ value }) => update("realm")(value)}
            />
            <Select
              label="Operation"
              options={webhookOperationOptions}
              value={component.operation}
              onChange={({ value }) => update("operation")(value)}
            />
          </Row>
          <Checkbox
            value={component.asyncLambda ?? false}
            onChange={update("asyncLambda")}
            label="Invoke Lambda Asynchronously (for long running webhooks)"
          />
          <WebhookQueryInput
            value={component.query}
            onChange={update("query")}
            pluginConfig={pluginConfig}
            topLevel={true}
          />
        </>
      )}

      {component.type === "schedule_config" && (
        <CronInput required value={component.cron} onChange={update("cron")} />
      )}

      {component.type === "metadata_view_config" && (
        <>
          <Select
            label="Object Type"
            value={component.objectType}
            options={objectTypeOptions}
            onChange={({ value }) => update("objectType")(value)}
          />
          <Select
            label="Category"
            value={component.category || null}
            options={
              component.objectType === "segments"
                ? categoryOptionsSegments
                : categoryOptions
            }
            onChange={({ value }) => update("category")(value)}
          />
        </>
      )}
    </Section>
  );
};

export default ComponentForm;
